import { useState, useCallback } from 'react';

import {
  FiInfo,
  FiUsers,
  FiCalendar,
  FiFile,
  FiChevronRight,
  FiClock,
  FiHeart,
  FiDownload,
  FiFileText,
  FiImage,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { useBroadcastQuery } from '@hooks';
import { SpeakerData } from '@common/types/api/speakers';
import { ScheduleData } from '@common/types/api/schedule';
import { RoomArchiveData } from '@common/types/api';

import * as S from './styles';

type TabId = 'about' | 'speakers' | 'schedule' | 'files';

type Tab = {
  id: TabId;
  labelKey: string;
  icon: typeof FiInfo;
};

const TABS: Tab[] = [
  { id: 'about', labelKey: 'pages.broadcast.info.about', icon: FiInfo },
  { id: 'speakers', labelKey: 'pages.broadcast.info.speakers', icon: FiUsers },
  {
    id: 'schedule',
    labelKey: 'pages.broadcast.info.schedule',
    icon: FiCalendar,
  },
  { id: 'files', labelKey: 'pages.broadcast.info.files', icon: FiFile },
];

// Helper to get initials from name
const getInitials = (name: string) => {
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Helper to format time
const formatTime = (time: string) => {
  if (!time) return '';
  return time.substring(0, 5);
};

// Helper to get day info from date string
const getDayInfo = (dateString: string, locale: string) => {
  const date = new Date(dateString);
  const dayName = date.toLocaleDateString(locale, { weekday: 'short' });
  const dayNumber = date.getDate();
  return { dayName: dayName.toUpperCase().replace('.', ''), dayNumber };
};

// Helper to get file extension and type
const getFileInfo = (
  filename: string,
): { extension: string; type: 'doc' | 'pdf' | 'image' | 'default' } => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';

  if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) {
    return { extension: ext.toUpperCase(), type: 'doc' };
  }
  if (ext === 'pdf') {
    return { extension: 'PDF', type: 'pdf' };
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
    return { extension: ext.toUpperCase(), type: 'image' };
  }
  return { extension: ext.toUpperCase() || 'FILE', type: 'default' };
};

// Get icon for file type
const getFileIcon = (type: 'doc' | 'pdf' | 'image' | 'default') => {
  switch (type) {
    case 'doc':
      return FiFileText;
    case 'pdf':
      return FiFile;
    case 'image':
      return FiImage;
    default:
      return FiFile;
  }
};

// Sub-components
type DescriptionTabProps = {
  description?: string;
  emptyMessage: string;
};

const DescriptionTab = ({ description, emptyMessage }: DescriptionTabProps) => (
  <S.TabContent>
    {description ? (
      <S.ContentText dangerouslySetInnerHTML={{ __html: description }} />
    ) : (
      <S.EmptyState>{emptyMessage}</S.EmptyState>
    )}
  </S.TabContent>
);

type SpeakersTabProps = {
  speakers?: SpeakerData[];
  emptyMessage: string;
  onSpeakerClick: (speakerId: number) => void;
};

const SpeakersTab = ({
  speakers,
  emptyMessage,
  onSpeakerClick,
}: SpeakersTabProps) => (
  <S.TabContent>
    {speakers && speakers.length > 0 ? (
      <S.SpeakersList>
        {speakers.map((speaker) => (
          <S.SpeakerItem
            key={speaker.id}
            onClick={() => onSpeakerClick(speaker.id)}
          >
            {speaker.photo ? (
              <S.SpeakerPhoto src={speaker.photo} alt={speaker.name} />
            ) : (
              <S.SpeakerAvatar>{getInitials(speaker.name)}</S.SpeakerAvatar>
            )}
            <S.SpeakerInfo>
              <S.SpeakerName>{speaker.name}</S.SpeakerName>
              {speaker.title && <S.SpeakerRole>{speaker.title}</S.SpeakerRole>}
            </S.SpeakerInfo>
            <S.SpeakerChevron>
              <FiChevronRight />
            </S.SpeakerChevron>
          </S.SpeakerItem>
        ))}
      </S.SpeakersList>
    ) : (
      <S.EmptyState>{emptyMessage}</S.EmptyState>
    )}
  </S.TabContent>
);

type ScheduleTabProps = {
  schedules?: ScheduleData[];
  emptyMessage: string;
  reminderLabel: string;
  locale: string;
  onScheduleClick: (scheduleId: number) => void;
};

const ScheduleTab = ({
  schedules,
  emptyMessage,
  reminderLabel,
  locale,
  onScheduleClick,
}: ScheduleTabProps) => (
  <S.TabContent>
    {schedules && schedules.length > 0 ? (
      <S.ScheduleList>
        {schedules.map((item) => {
          const { dayName, dayNumber } = getDayInfo(item.schedule_date, locale);
          return (
            <S.ScheduleItem
              key={item.id}
              onClick={() => onScheduleClick(item.id)}
            >
              <S.ScheduleDateColumn>
                <S.ScheduleDayName>{dayName}</S.ScheduleDayName>
                <S.ScheduleDayNumber>{dayNumber}</S.ScheduleDayNumber>
              </S.ScheduleDateColumn>
              <S.ScheduleContent>
                <S.ScheduleTitle>{item.title}</S.ScheduleTitle>
                {item.description && (
                  <S.ScheduleDescription>
                    {item.description}
                  </S.ScheduleDescription>
                )}
                <S.ScheduleFooter>
                  <S.ScheduleTime>
                    <FiClock />
                    {formatTime(item.start_time)}
                    {item.end_time && ` - ${formatTime(item.end_time)}`}
                  </S.ScheduleTime>
                  <S.ScheduleReminderButton type="button">
                    <FiHeart />
                    {reminderLabel}
                  </S.ScheduleReminderButton>
                </S.ScheduleFooter>
              </S.ScheduleContent>
              <S.ScheduleChevron>
                <FiChevronRight />
              </S.ScheduleChevron>
            </S.ScheduleItem>
          );
        })}
      </S.ScheduleList>
    ) : (
      <S.EmptyState>{emptyMessage}</S.EmptyState>
    )}
  </S.TabContent>
);

type FilesTabProps = {
  files?: RoomArchiveData[];
  emptyMessage: string;
};

const FilesTab = ({ files, emptyMessage }: FilesTabProps) => (
  <S.TabContent>
    {files && files.length > 0 ? (
      <S.FilesList>
        {files.map((file) => {
          const { extension, type } = getFileInfo(
            file.url_download || file.description,
          );
          const FileIcon = getFileIcon(type);

          return (
            <S.FileItem key={file.id}>
              <S.FileIconWrapper $type={type}>
                <FileIcon />
              </S.FileIconWrapper>
              <S.FileInfo>
                <S.FileName>{file.description}</S.FileName>
                <S.FileMeta>{extension}</S.FileMeta>
              </S.FileInfo>
              <S.FileDownloadButton
                href={file.url_download}
                target="_blank"
                rel="noopener noreferrer"
                download
              >
                <FiDownload />
              </S.FileDownloadButton>
            </S.FileItem>
          );
        })}
      </S.FilesList>
    ) : (
      <S.EmptyState>{emptyMessage}</S.EmptyState>
    )}
  </S.TabContent>
);

const InfoTabs = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('about');
  const history = useHistory();
  const { search, ...location } = useLocation();

  const handleScheduleClick = useCallback(
    (scheduleId: number) => {
      const queryParams = new URLSearchParams(search);
      queryParams.set('session', String(scheduleId));
      history.push({
        ...location,
        search: `?${queryParams.toString()}`,
      });
    },
    [history, location, search],
  );

  const handleSpeakerClick = useCallback(
    (speakerId: number) => {
      const queryParams = new URLSearchParams(search);
      queryParams.set('speaker', String(speakerId));
      history.push({
        ...location,
        search: `?${queryParams.toString()}`,
      });
    },
    [history, location, search],
  );

  const { data: description } = useBroadcastQuery({
    select: ({ description: desc }) => desc,
    notifyOnChangeProps: ['data'],
  });

  const { data: speakers } = useBroadcastQuery({
    select: ({ participants }) => participants.data,
    notifyOnChangeProps: ['data'],
  });

  const { data: schedules } = useBroadcastQuery({
    select: ({ schedules: sched }) => sched.data,
    notifyOnChangeProps: ['data'],
  });

  const { data: files } = useBroadcastQuery({
    select: ({ archives }) => archives.data,
    notifyOnChangeProps: ['data'],
  });

  const { data: filesCount } = useBroadcastQuery({
    select: ({ archives_total }) => archives_total,
    notifyOnChangeProps: ['data'],
  });

  const renderContent = () => {
    switch (activeTab) {
      case 'about':
        return (
          <DescriptionTab
            description={description}
            emptyMessage={t('pages.broadcast.info.noDescription')}
          />
        );
      case 'speakers':
        return (
          <SpeakersTab
            speakers={speakers}
            emptyMessage={t('pages.broadcast.info.noSpeakers')}
            onSpeakerClick={handleSpeakerClick}
          />
        );
      case 'schedule':
        return (
          <ScheduleTab
            schedules={schedules}
            emptyMessage={t('pages.broadcast.info.noSchedule')}
            reminderLabel={t('pages.schedule.reminder')}
            locale={i18n.language}
            onScheduleClick={handleScheduleClick}
          />
        );
      case 'files':
        return (
          <FilesTab
            files={files}
            emptyMessage={t('pages.broadcast.info.noFiles')}
          />
        );
      default:
        return null;
    }
  };

  return (
    <S.Container>
      <S.TabsList>
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isFilesTab = tab.id === 'files';
          const showBadge =
            isFilesTab && filesCount !== undefined && filesCount > 0;

          return (
            <S.Tab
              key={tab.id}
              $active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              type="button"
            >
              <Icon />
              <span>{t(tab.labelKey)}</span>
              {showBadge && <S.TabBadge>{filesCount}</S.TabBadge>}
            </S.Tab>
          );
        })}
      </S.TabsList>

      {renderContent()}
    </S.Container>
  );
};

export default InfoTabs;
